'use client';

import { type Ref, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { usePathname, useRouter } from 'next/navigation';
import type { Selection } from '@react-types/shared';
import type {
  Asset,
  QuickSearchResult,
} from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import { getPageType } from '@frameio/shared/routing';
import { getUploadDisplayDate } from '@frameio/shared/utils';
import type { OnRequestClose } from '@frameio/vapor';
import { ListItem, Text, useIsAppleOs, usePrevious } from '@frameio/vapor';
import { useQuickSearchModal } from '../../../shared/contexts';
import {
  useSearchSession,
  useTrackSearchQueryInitiated,
  useTrackSearchResultsClicked,
} from '../../../shared/contexts/SearchAnalyticsContext';
import type { TrackResultType } from '../../../shared/contexts/SearchAnalyticsContext/types';
import { useIsNLPEnabled } from '../../../shared/hooks';
import {
  getDaysSinceCreated,
  getMatchTypeTrackingValues,
  getResultItemLink,
} from '../../../shared/utils';
import {
  useNavigateToAdvancedSearch,
  useQuickSearchResultsAnalytics,
} from '../../hooks';
import { useQuickSearchAppliedFilterTypes } from '../../hooks/useQuickSearchAppliedFilterTypes';
import { useQuickSearchMachineSingle } from '../../machines/hooks/useQuickSearchMachineSingle';
import type { QuickSearchMachineOutput } from '../../machines/types';
import { getActiveListItemDetails } from '../../utils';
import { QuickSearchListItem } from '../QuickSearchListItem';
import { LIST_ITEM_CARD_PADDING } from '../QuickSearchListItem/components/ListItemCard';
import type { QuickSearchResultTypes } from '../shared/QuickSearchResultTypes';
import { AdvancedSearchItem } from './AdvancedSearchItem';
import {
  ADVANCED_AI_SEARCH_ITEM_KEY,
  ADVANCED_SEARCH_ITEM_KEY,
} from './constants';
import TEXT from './generated/TEXT/QuickSearchCompactContent';
import styles from './QuickSearchCompactContent.module.css';
import { QuickSearchCompactList } from './QuickSearchCompactList';

TEXT.DEFINE({
  /** Accessible label for individual search result item */
  listItemAriaLabel: (name: string): string => `Search result: ${name}`,
  /** Accessible label for the advanced search item */
  advancedSearchItemAriaLabel: (query: string, modifier: string): string =>
    `Show all results for ${query}, press ${modifier} Enter to navigate`,
  /** Accessible label for the AI search item */
  advancedAiSearchItemAriaLabel: (query: string, modifier: string): string =>
    `Show AI results for ${query}, press ${modifier} Enter to navigate`,
  /** Message displayed when there are no lexical search results */
  noNameMatchFound: 'No name match found',
  /** Accessible label for the entire quick search component */
  quickSearchAriaLabel: 'Quick Search',
  /** Compact results list heading showing how many standard matches were found */
  resultsListSectionTitle: (count: number): string =>
    plural(count, `${count} results`, '1 result'),
});

export type QuickSearchCompactContentProps = {
  initialFocusInputRef?: Ref<HTMLInputElement>;
  isMobile: boolean;
  onDialogClose: OnRequestClose;
};

function generateQuickSearchResultListItem(
  item: QuickSearchResult,
  position: number
): string | undefined {
  return item?.__typename
    ? `${item.__typename}:${item.result.id}:${position}`
    : undefined;
}

export function QuickSearchCompactContent({
  initialFocusInputRef,
  isMobile,
  onDialogClose,
}: QuickSearchCompactContentProps) {
  const router = useRouter();

  const {
    isModalOpen,
    query: searchQuery,
    setQuery,
    clearQuery,
    requestVariables,
    focusedKey,
    setFocusedKey,
    scopedToProjectId,
    closeModal,
  } = useQuickSearchModal();

  const isNlpEnabled = useIsNLPEnabled();

  const [isInputQueryStale, setIsInputQueryStale] = useState(false);
  const [inputQuery, setInputQuery] = useState(searchQuery);
  const previousSearchQuery = usePrevious(searchQuery);
  const previousScopedToProjectId = usePrevious(scopedToProjectId);
  const [isInitial, setIsInitial] = useState(true);
  const appliedFilterTypes = useQuickSearchAppliedFilterTypes();

  useEffect(() => {
    const hasSearchQueryChanged =
      previousSearchQuery !== undefined && searchQuery !== previousSearchQuery;
    const hasScopeChanged =
      previousScopedToProjectId !== undefined &&
      scopedToProjectId !== previousScopedToProjectId;
    if (hasSearchQueryChanged || hasScopeChanged) {
      setIsInitial(false);
    }
  }, [
    searchQuery,
    previousSearchQuery,
    scopedToProjectId,
    previousScopedToProjectId,
  ]);

  const { items, loadingStatus, hasQueriesSettled, standardResultsCount } =
    useQuickSearchMachineSingle({
      requestVariables,
      skip: !requestVariables.accountId || searchQuery === '',
      isSearchQueryStale: isInputQueryStale,
      isInitial,
    }) as Extract<QuickSearchMachineOutput, { listMode: 'SINGLE' }>;

  const isPrevModalOpen = usePrevious(isModalOpen);
  const trackSearchQueryInitiated = useTrackSearchQueryInitiated();
  const trackSearchResultsClicked = useTrackSearchResultsClicked();
  const {
    resetSearchSession,
    setSearchInitiatedAt,
    resetSearchInitiatedAt,
    setSearchQuery,
  } = useSearchSession();

  const handleTrackResultClick = useCallback(
    (resultItem: QuickSearchResult) => {
      if (searchQuery.length === 0 || items.length === 0) return;

      const resultPosition = items.indexOf(resultItem) + 1;
      const resultName = resultItem.result.name;
      let resultType: TrackResultType;
      if (resultItem.__typename === 'ProjectResult') {
        resultType = 'Project';
      } else if ((resultItem.result as Asset).assetType === 'FOLDER') {
        resultType = 'Folder';
      } else {
        resultType = 'Asset';
      }

      let dateForTracking: string | null | undefined;
      if (resultItem.__typename === 'ProjectResult') {
        dateForTracking = resultItem.result.insertedAt;
      } else if (resultItem.__typename === 'AssetResult') {
        dateForTracking = getUploadDisplayDate(resultItem.result);
      }

      const daysSinceCreated = getDaysSinceCreated(dateForTracking);

      trackSearchResultsClicked({
        daysSinceUploaded: daysSinceCreated,
        filterApplied: appliedFilterTypes,
        matchType: getMatchTypeTrackingValues(resultItem.matches?.nodes ?? []),
        resultName,
        resultPosition,
        resultSource: 'Standard',
        resultType,
        searchQuery,
        tabSelected: undefined,
      });
    },
    [appliedFilterTypes, items, searchQuery, trackSearchResultsClicked]
  );

  /**
   * Tracked imperatively at dispatch time rather than in a useEffect keyed off
   * isInputQueryStale: that flag is relayed up from the list component through
   * its own effect, one render behind the raw keystroke comparison, so it can
   * still be true here when results-displayed (gated only on hasQueriesSettled)
   * has already fired for the same query. Firing here guarantees query-initiated
   * always precedes the fetch it triggers.
   */
  const lastTrackedQueryRef = useRef<string | null>(null);

  const handleRequestSearch = useCallback(
    (query: string) => {
      setQuery(query);
      setSearchQuery(query);

      const trimmedQuery = query.trim();
      if (trimmedQuery && lastTrackedQueryRef.current !== trimmedQuery) {
        lastTrackedQueryRef.current = trimmedQuery;
        trackSearchQueryInitiated({
          filterApplied: appliedFilterTypes,
          searchQuery: trimmedQuery,
        });
        setSearchInitiatedAt(performance.now());
      }
    },
    [
      appliedFilterTypes,
      setQuery,
      setSearchInitiatedAt,
      setSearchQuery,
      trackSearchQueryInitiated,
    ]
  );

  const handleInputQueryStaleChange = useCallback((isStale: boolean) => {
    setIsInputQueryStale(isStale);
  }, []);

  const handleInputQueryChange = useCallback((query: string) => {
    setInputQuery(query);
  }, []);

  const handleResetQueryState = useCallback(() => {
    clearQuery();
    resetSearchInitiatedAt();
    setSearchQuery('');
    setIsInitial(false);
    lastTrackedQueryRef.current = null;
  }, [clearQuery, resetSearchInitiatedAt, setSearchQuery]);

  const displayedQuery = inputQuery || searchQuery;

  const navigateToAdvancedSearch = useNavigateToAdvancedSearch();
  // Handles navigation to advanced search when the input query is considered settled
  const pendingNavRef = useRef(false);

  // Only default to the AI tab once we know the standard results have
  // actually settled and are consisdered empty
  // @todo (SRCH-2455): Rename this advanced-search target to mode when retiring the tab navigation input.
  const advancedSearchTab =
    hasQueriesSettled && items.length === 0 ? 'ai' : undefined;

  const handleNavigateToAdvancedSearch = useCallback(
    (event?: React.KeyboardEvent | React.MouseEvent) => {
      // Defer navigation until the input query is considered settled
      if (isInputQueryStale) {
        pendingNavRef.current = true;
        return;
      }

      navigateToAdvancedSearch({
        query: displayedQuery,
        // @todo (SRCH-2455): Replace the advanced-search tab input with mode after rollout.
        tab: advancedSearchTab,
        event,
      });
    },
    [
      navigateToAdvancedSearch,
      displayedQuery,
      isInputQueryStale,
      advancedSearchTab,
    ]
  );

  const handleNavigateToAdvancedAiSearch = useCallback(
    (event?: React.KeyboardEvent | React.MouseEvent) => {
      navigateToAdvancedSearch({
        query: displayedQuery,
        // @todo (SRCH-2455): Replace the advanced-search tab input with mode after rollout.
        tab: 'ai',
        event,
      });
    },
    [navigateToAdvancedSearch, displayedQuery]
  );

  useEffect(() => {
    if (!isInputQueryStale && pendingNavRef.current) {
      pendingNavRef.current = false;
      navigateToAdvancedSearch({
        query: displayedQuery,
        // @todo (SRCH-2455): Replace the deferred advanced-search tab input with mode after rollout.
        tab: advancedSearchTab,
      });
    }
  }, [
    isInputQueryStale,
    displayedQuery,
    advancedSearchTab,
    navigateToAdvancedSearch,
  ]);

  const handleNavigateToItem = useCallback(
    (resultItem: QuickSearchResult) => {
      const href = getResultItemLink(
        resultItem.result,
        resultItem.__typename as QuickSearchResultTypes,
        null
      );
      if (!href) return;
      handleTrackResultClick(resultItem);
      closeModal({ navigatingTo: href });
      router.push(href);
    },
    [closeModal, handleTrackResultClick, router]
  );

  const handleOnActivateItem = useCallback(
    (activeKey?: string, event?: React.KeyboardEvent | React.MouseEvent) => {
      const key = activeKey || focusedKey;
      const { isItemSelected, itemId } = getActiveListItemDetails(
        key ? String(key) : null
      );

      if (!isItemSelected) return;

      if (key === 'advanced-search-item') {
        handleNavigateToAdvancedSearch(event);
        return;
      }

      if (key === ADVANCED_AI_SEARCH_ITEM_KEY) {
        handleNavigateToAdvancedAiSearch(event);
        return;
      }

      const item = items.find((result) => result.result.id === itemId);
      if (!item) return;

      handleNavigateToItem(item);
    },
    [
      items,
      focusedKey,
      handleNavigateToItem,
      handleNavigateToAdvancedSearch,
      handleNavigateToAdvancedAiSearch,
    ]
  );

  const handleOnRequestClose: OnRequestClose = useCallback(
    (evt) => {
      handleResetQueryState();
      onDialogClose(evt);
    },
    [onDialogClose, handleResetQueryState]
  );

  const handleOnSelectionChange = useCallback(
    (keys: Selection) => {
      const newlySelectedId = Array.from(keys)[0] as string;
      if (!newlySelectedId || newlySelectedId === focusedKey) return;
      setFocusedKey(newlySelectedId);
    },
    [setFocusedKey, focusedKey]
  );

  const renderListItem = useCallback(
    (resultItem: QuickSearchResult) => {
      const { result: item, __typename: type } = resultItem;
      const position = items.indexOf(resultItem) + 1;
      const textValue = TEXT.listItemAriaLabel(item.name);

      return (
        <ListItem
          key={generateQuickSearchResultListItem(resultItem, position)}
          aria-label={textValue}
          as="div"
        >
          <div
            onClick={(event) => {
              const target = event.target as Element;
              if (!event.currentTarget.contains(target)) return;
              if (target.closest('button')) return;
              handleNavigateToItem(resultItem);
            }}
          >
            <QuickSearchListItem
              itemId={item.id}
              itemPosition={position}
              itemType={type as QuickSearchResultTypes}
              paddingInlineEnd={LIST_ITEM_CARD_PADDING}
              searchQuery={displayedQuery}
            />
          </div>
        </ListItem>
      );
    },
    [displayedQuery, items, handleNavigateToItem]
  );

  const pathname = usePathname();
  const isOnSearchPage = getPageType(pathname) === 'search';
  useEffect(() => {
    if (isPrevModalOpen && !isModalOpen && !isOnSearchPage) {
      resetSearchSession();
    }
  }, [isModalOpen, isPrevModalOpen, resetSearchSession, isOnSearchPage]);

  const hasSearchQuery = searchQuery.trim() !== '';
  const searchScope = scopedToProjectId ? 'CurrentProject' : 'AllProjects';

  useQuickSearchResultsAnalytics({
    hasSearchQuery,
    searchQuery,
    hasQueriesSettled,
    standardResultsCount,
    searchScope,
  });

  const isAppleOs = useIsAppleOs();

  const advancedSearchItemAriaLabel = TEXT.advancedSearchItemAriaLabel(
    displayedQuery,
    isAppleOs ? 'Command' : 'Control'
  );

  const advancedAiSearchItemAriaLabel = TEXT.advancedAiSearchItemAriaLabel(
    displayedQuery,
    isAppleOs ? 'Command' : 'Control'
  );

  const isLoading = loadingStatus === 'loading';
  const hasStandardResults = items?.length > 0 || isLoading;

  return (
    <QuickSearchCompactList
      aria-label={TEXT.quickSearchAriaLabel}
      focusedKey={focusedKey}
      hasAdvancedAiSearchItem={isNlpEnabled}
      hasQueriesSettled={hasQueriesSettled}
      initialFocusInputRef={initialFocusInputRef}
      isInitial={isInitial}
      isMobile={isMobile}
      items={items}
      loading={loadingStatus}
      NoResults={
        !hasStandardResults && (
          <div
            className={clsx(
              styles.noResultsContainer,
              isNlpEnabled && styles.hasAiSearchItem
            )}
          >
            <Text color="tertiary" variant="body">
              {TEXT.noNameMatchFound}
            </Text>
          </div>
        )
      }
      onActivateItem={handleOnActivateItem}
      onInputQueryChange={handleInputQueryChange}
      onInputQueryStaleChange={handleInputQueryStaleChange}
      onRequestClose={handleOnRequestClose}
      onRequestSearch={handleRequestSearch}
      onResetQueryState={handleResetQueryState}
      onSelectionChange={handleOnSelectionChange}
      searchQuery={searchQuery}
    >
      <>
        {hasStandardResults && (
          <ListItem
            key={ADVANCED_SEARCH_ITEM_KEY}
            aria-label={advancedSearchItemAriaLabel}
            as="div"
            className={clsx(
              styles.advancedSearchItemListItem,
              hasStandardResults && !isNlpEnabled && styles.bottomPadding
            )}
            textValue={advancedSearchItemAriaLabel}
          >
            <AdvancedSearchItem
              isAppleOs={isAppleOs}
              onClick={handleNavigateToAdvancedSearch}
              searchQuery={displayedQuery}
            />
          </ListItem>
        )}
        {isNlpEnabled && (
          <ListItem
            key={ADVANCED_AI_SEARCH_ITEM_KEY}
            aria-label={advancedAiSearchItemAriaLabel}
            as="div"
            className={clsx(
              styles.advancedSearchItemListItem,
              hasStandardResults && styles.bottomPadding
            )}
            textValue={advancedAiSearchItemAriaLabel}
          >
            <AdvancedSearchItem
              isAiSearch={true}
              isAppleOs={isAppleOs}
              onClick={handleNavigateToAdvancedAiSearch}
              searchQuery={displayedQuery}
            />
          </ListItem>
        )}
        {items.map(renderListItem)}
      </>
    </QuickSearchCompactList>
  );
}
